import Empleados from './empleados.vue';
import EmpleadoForm from './empleados-form.vue';
import Error403 from './../403.vue';
import RoutesUtils from '../../js/RoutesUtils.js';
import PermisosEmpleados from './permisos.js';
import { conUsuarioActualizado } from '../../js/barreraUsuarioActualizado.js';

/**
 * [#65, 02/10/2026] Las cuatro rutas entran por la barrera de usuario actualizado y no por
 * `RoutesUtils.checkPermission`: el permiso se resuelve con el `Usuario.me()` de esta carga y no
 * con el menú que quedó guardado de la sesión anterior, y la ficha recibe ya decidido lo que puede
 * enseñar. El porqué, en `barreraUsuarioActualizado.js`.
 *
 * Es el módulo donde viven los datos personales de la plantilla —y, dentro de la ficha, la
 * tarjeta de vacaciones—, así que es donde decidir con un permiso viejo tiene consecuencias.
 */

/** Deniega o deja pasar con el permiso ya fresco. `null` significa «no entra». */
function pantallaSiTieneAcceso(path, roles, Comp, props, rolesDirectos) {
    if (!RoutesUtils.tieneAcceso(path, roles, rolesDirectos)) {
        return { component: Error403 };
    }
    return { component: Comp, props: props || {} };
}

const routes = [{
        path: '/empleados/',
        beforeEnter: RoutesUtils.checkAuthPage,
        // [#932] El listado también borra, así que también tiene que saber si puede quitar el acceso.
        async: conUsuarioActualizado(function(routeTo) {
            return pantallaSiTieneAcceso(routeTo.path, PermisosEmpleados.ROLES_MODULO, Empleados,
                { puedeCambiarAcceso: PermisosEmpleados.cambiarAcceso() });
        }),
        keepAlive: true
    },
    {
        path: '/empleados/new/',
        beforeEnter: RoutesUtils.checkAuthPage,
        // Quien entra a dar de alta o a editar tiene el módulo —es el permiso que acaba de pasar
        // esta misma línea—, así que la ficha se monta entera. Va escrito y no deducido dentro de
        // la pantalla: un formulario de alta a medias no tendría ningún sentido.
        // El rol y el acceso a la ERP, en cambio, no vienen con el módulo: son solo de ADMIN (#932).
        async: conUsuarioActualizado(function(routeTo) {
            return pantallaSiTieneAcceso(routeTo.path, PermisosEmpleados.ROLES_MODULO, EmpleadoForm,
                { verFichaCompleta: true, puedeGestionar: true,
                    puedeCambiarAcceso: PermisosEmpleados.cambiarAcceso() });
        })
    },
    {
        path: '/empleados/edit/:id/',
        beforeEnter: RoutesUtils.checkAuthPage,
        async: conUsuarioActualizado(function(routeTo) {
            return pantallaSiTieneAcceso(routeTo.path, PermisosEmpleados.ROLES_MODULO, EmpleadoForm,
                { verFichaCompleta: true, puedeGestionar: true,
                    puedeCambiarAcceso: PermisosEmpleados.cambiarAcceso() });
        })
    },
    {
        path: '/empleados/view/:id/',
        // DIRECTOR entra aquí por la tarjeta de vacaciones, que la API le deja ver y editar de
        // cualquiera (ADMIN/DIRECTOR, I3 de la #47) y que vive dentro de esta ficha. Va además
        // como rol directo —el quinto argumento— porque a esta ficha llega enlazado desde el
        // Mapa de aprobación y el módulo de empleados no está en su menú: con el menú cargado,
        // el criterio de siempre lo mandaba a la página 403. Solo la ficha de lectura: el alta y
        // la edición del empleado siguen siendo de ADMIN e INSPECTOR.
        //
        // Las listas de roles salen de `permisos.js` y no están escritas aquí: la ficha y el Mapa
        // de aprobación hacen la misma pregunta y tienen que responderla igual (revisión #1711).
        //
        // Y los dos permisos de dentro de la ficha —ver la ficha entera y poder gestionarla—
        // viajan como `prop` resueltos aquí, con el usuario ya fresco. Antes los resolvía la
        // propia pantalla y los revisaba con un temporizador: ver `barreraUsuarioActualizado.js`.
        beforeEnter: RoutesUtils.checkAuthPage,
        async: conUsuarioActualizado(function(routeTo) {
            return pantallaSiTieneAcceso(routeTo.path, PermisosEmpleados.ROLES_FICHA_LECTURA,
                EmpleadoForm, {
                    editable: false,
                    verFichaCompleta: PermisosEmpleados.verFichaCompleta(),
                    puedeGestionar: PermisosEmpleados.gestionar(),
                    puedeCambiarAcceso: PermisosEmpleados.cambiarAcceso()
                }, PermisosEmpleados.ROLES_DIRECTOS_FICHA_LECTURA);
        })
    },
];
export default routes;
